import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { RequesterSpend } from '@/hooks/useSpendAnalytics';
import { Loader2, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface SpendByRequesterTableProps {
  data: RequesterSpend[] | undefined;
  isLoading: boolean;
}

type SortKey = 'name' | 'jobCount' | 'totalSpend' | 'avgJobValue' | 'percentage';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 10;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function SpendByRequesterTable({ data, isLoading }: SpendByRequesterTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('totalSpend');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedData = [...(data || [])].sort((a, b) => {
    let comparison = 0;
    switch (sortKey) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'jobCount':
        comparison = a.jobCount - b.jobCount;
        break;
      case 'totalSpend':
        comparison = a.totalSpendCents - b.totalSpendCents;
        break;
      case 'avgJobValue':
        comparison = a.avgJobValue - b.avgJobValue;
        break;
      case 'percentage':
        comparison = a.percentage - b.percentage;
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const SortableHeader = ({ label, sortKey: key }: { label: string; sortKey: SortKey }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(key)}
    >
      {label}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Spend by Requester</CardTitle>
          <CardDescription>Breakdown of spending by team member</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Spend by Requester</CardTitle>
          <CardDescription>Breakdown of spending by team member</CardDescription>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <p className="text-muted-foreground">No requester data for this period</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Spend by Requester</CardTitle>
        <CardDescription>Breakdown of spending by team member</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader label="Requester Name" sortKey="name" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader label="# of Jobs" sortKey="jobCount" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader label="Total Spend" sortKey="totalSpend" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader label="Avg Job Value" sortKey="avgJobValue" />
                </TableHead>
                <TableHead className="text-right">
                  <SortableHeader label="% of Total" sortKey="percentage" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((requester) => (
                <TableRow key={requester.userId}>
                  <TableCell className="font-medium">{requester.name}</TableCell>
                  <TableCell className="text-right">{requester.jobCount}</TableCell>
                  <TableCell className="text-right">{formatCurrency(requester.totalSpend)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(requester.avgJobValue)}</TableCell>
                  <TableCell className="text-right">{requester.percentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
              {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} of {sortedData.length} entries
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
